import { z } from 'zod';
import { refersTo } from '../common/refers-to';
import { ApplySkippedFieldSchema } from './document-analysis.schemas';
import { DossierFactValueTypeSchema, DossierNumberFormatSchema } from './dossier.schemas';
import { FactKeyGroupSchema } from './admin-fact-keys.schemas';
import { FactOriginSchema, FactReviewLevelSchema, FactStatusSchema } from './fact-provenance.schemas';

// The evidence a reviewer can open. A document carries its name and type so the app can
// preview it without a second read; `document` is null when it was deleted since.
export const FactReviewEvidenceSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('DOCUMENT'),
    documentId: z.string(),
    page: z.number().int().positive().nullable(),
    document: z.object({ displayName: z.string(), contentType: z.string() }).nullable(),
  }),
  z.object({ kind: z.literal('EXTERNAL'), source: z.string(), date: z.string() }),
]);

// The Objekt field a confirmation fills. `current` is the field's value now (null = empty);
// `replaces` says confirming would change an entered value — the server's own rule, so the
// app never re-derives it.
export const FactReviewObjektFieldSchema = z.object({
  propertyFieldKey: z.string(),
  current: z.unknown(),
  replaces: z.boolean(),
});

export const FactReviewItemSchema = z.object({
  factId: z.string(),
  factKey: z.string(),
  label: z.string(),
  groupId: FactKeyGroupSchema,
  valueType: DossierFactValueTypeSchema,
  numberFormat: DossierNumberFormatSchema.nullable(),
  value: z.unknown(), // null for UNKNOWN / NOT_APPLICABLE
  occurrence: z.string().nullable(),
  origin: FactOriginSchema,
  status: FactStatusSchema,
  evidence: FactReviewEvidenceSchema.nullable(),
  timeReference: z.string().nullable(), // YYYY-MM or YYYY-MM-DD
  recordedAt: z.string(), // ISO instant, the fact's extracted_at
  objektField: FactReviewObjektFieldSchema.nullable(),
});

// One value shown on the Objekt, with the review level of the findings behind it (IMM-1404.2).
// `value` is coerced to the Objekt field's own type, so a section formats it like its own value.
export const FactFieldMarkerSchema = z.object({
  propertyFieldKey: z.string(),
  factKey: z.string(),
  label: z.string(), // the catalog label, for a row that combines two fields
  reviewLevel: FactReviewLevelSchema,
  status: FactStatusSchema, // strongest of its findings: CONTRADICTED > ASSUMED > PROVEN
  value: z.unknown(),
  fieldEmpty: z.boolean(), // true: the field is empty and the findings' value stands in
  factIds: z.array(z.string()).min(1),
  withdrawable: z.boolean(),
});

export const PropertyFactReviewSchema = z.object({
  propertyId: refersTo('property', z.string()),
  open: z.array(FactReviewItemSchema),
  markers: z.array(FactFieldMarkerSchema),
});

export const ConfirmFactsBodySchema = z
  .object({
    factIds: z
      .array(z.uuid())
      .min(1)
      .max(1000)
      .transform((ids) => [...new Set(ids)]),
    replaceFieldKeys: z
      .array(z.string().min(1))
      .max(64)
      .default([])
      .transform((keys) => [...new Set(keys)]),
  })
  .strict();

export const ConfirmFactsResponseSchema = z.object({
  confirmedFactIds: z.array(z.string()),
  appliedFieldKeys: z.array(z.string()),
  skippedFields: z.array(ApplySkippedFieldSchema),
});

export const WithdrawFactConfirmationsBodySchema = z
  .object({
    factIds: z
      .array(z.uuid())
      .min(1)
      .max(100)
      .transform((ids) => [...new Set(ids)]),
  })
  .strict();

export const WithdrawFactConfirmationsResponseSchema = z.object({
  withdrawnFactIds: z.array(z.string()),
});

export type PropertyFactReview = z.infer<typeof PropertyFactReviewSchema>;
export type FactReviewItem = z.infer<typeof FactReviewItemSchema>;
export type ConfirmFactsBody = z.infer<typeof ConfirmFactsBodySchema>;
export type ConfirmFactsResponse = z.infer<typeof ConfirmFactsResponseSchema>;
export type FactFieldMarker = z.infer<typeof FactFieldMarkerSchema>;
export type WithdrawFactConfirmationsResponse = z.infer<typeof WithdrawFactConfirmationsResponseSchema>;
